import React, {Fragment} from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import MUI_TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";

import { getComparator, tableHeightPerTotalPages } from "@helpers/muiTable";
import i18n from "@helpers/i18n";

import ContentBox from "@components/ContentBox/ContentBox";
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";
import CustomTableFooter from "@cobComponents/CustomTableFooter";
import searchIcon from "@assets/img/search_icon.svg";
import TableExpanderIcon from "@components/TableExpanderIcon";
import ExpandableRow from "@views/Dashboard/ExpandableRow";

import useFuturTable from "./useFuturTable";
import CustomerModal from "@cobComponents/CustomerModal/CustomerModal";
import AddCustomerModal from "@cobComponents/AddCustomerModal";
import DashboardTabs from "../DashboardTabs";
import EnhancedTableHead from "./EnhancedTableHead";

const FuturTable = props => {
  const {
    user,
    type,
    partner,
    advisor,
    isCustomerModal,
    whitelabel,
    loaded,
    tabs,
    columns,
    copyList,
    numCases,
    page,
    setPage,
    searchString,
    setSearchString,
    order,
    orderBy,
    rowsPerPage,
    setRowsPerPage,
    setSelectedCustomer,
    handleChangePage,
    handleRequestSort,
    requestSearch,
    handleEntryClick,    
    onAction,
    currentAction,
    resetPaginationAndFilters,
    customerModalOpen,
    selectedCustomer,
    handleClientClick,
    handleClientModalClose,
    updateCustomers,
    caseFilterContext,
    addCustomerModalOpen,
    toggleAddCustomerModal,
    setCustomerState,
    isAdviceSession,
    onWorkflowSwitched,
    nextStepOptions
  } = useFuturTable(props);

  return (
    <ContentBox
      style={{
        display: "inline",
        maxHeight: "70vh",
        boxShadow: "none",
        margin: 0,
        padding: 0
      }}
    >
      <Box 
        sx={{
          width: "100%",
          alignItems: "flex-start"
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: {xs: "column", md: "row"},
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%"
          }}
        >
          <DashboardTabs 
            type="futur" 
            translationGroup="dashboard_futur"
            tabs={tabs}
            fetchingData={!loaded}
            setPage={setPage}
            setSearchString={setSearchString}
            caseFilter={caseFilterContext.caseFilter}
            setCaseFilter={caseFilterContext.setCaseFilter}
          />
          <MUI_TextField
            size="small"
            placeholder="Sök..."
            type="search"
            onInput={e => requestSearch(e.target.value)}
            InputProps={{
              value: searchString,
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    component="img"
                    sx={{
                      height: 20,
                      width: 20,
                      paddingBottom: 0.25
                    }}
                    alt="logo"
                    src={searchIcon}
                  />
                </InputAdornment>
              ),
              sx: {
                px: 3,
                fontSize: "13px",
                borderRadius: "20px",
                width: "100%",
              }
            }}
            sx={{ 
              width: {xs: "100%", md: "30%"},
            }}
          />
        </Box>
        <TableContainer component={Box} sx={{ overflowY: "hidden"}}>
          <Box
            sx={{
              display: "block",
              width: "100%",
              minHeight: tableHeightPerTotalPages[rowsPerPage] || "unset",
              ...(isCustomerModal && {maxHeight: "60vh"}),
              mt: 1,
              border: 0,
              overflow: "auto"
            }}
          >
            <Table 
              stickyHeader
              className="mui-table"
              aria-label="Futur Table"
              sx={{
                minWidth: 700,
                border: 0,
                tableLayout: "fixed"
              }}
            >
              <EnhancedTableHead
                columns={columns}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {!loaded ? (
                  <TableRow
                    sx={{
                      height: "100%",
                      "&:last-child td": { border: 0 }
                    }}
                  >
                    <TableCell colSpan={columns.length}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: tableHeightPerTotalPages[rowsPerPage]*0.7
                        }}
                      >
                        <ActivityIndicator color="#4cb7d5"  busy/>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <Fragment>
                    {copyList && copyList.sort(getComparator(order, orderBy))
                      .map((entry, key) => {
                        const isExpandable = (!isAdviceSession && entry.state === "draft" || entry.state === "sent") || entry.state === "signed";
                        
                        let entryState = tabs.find(tab => tab.key === entry.state);
                        if (!entryState) {
                          entryState = {
                            translationKey: "drafts",
                            color: "#F5F5F5"
                          }
                        }

                        return (
                          <Fragment key={key}>
                            <TableRow
                              sx={{
                                height: 48,
                                ...(key % 2 !== 0 && { "& > td": {background: "#f5f9ff" }}),
                              }}
                            >
                              <TableCell 
                                sx={{
                                  px: 0,
                                  pl: {xs: 0.5, md: 1},
                                  py: 0.5,
                                  borderBottom: "none",
                                }}
                              >
                                {isExpandable && (
                                  <Box
                                    onClick={() => handleEntryClick(entry.caseId)}
                                    sx={{
                                      cursor: "pointer"
                                    }}
                                  >
                                    <TableExpanderIcon
                                      toggled={entry?.toggled}
                                    />
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{
                                px: 0,
                                py: 0.5,
                                borderBottom: "none",
                                color: "secondary.lighter",
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                paddingLeft: "10px"
                              }}>
                                <Box sx={{
                                  display: "inline-block",
                                  backgroundColor: entryState.color,
                                  borderRadius: "9px",
                                  alignContent: "center",
                                  padding: "4px 8px",
                                  lineHeight: "10px",
                                        border: "1px solid #EFEFEF"
                                }}>
                                  <span style={{
                                    fontSize: "10px",
                                    textTransform: "uppercase",
                                    fontWeight: "bold",
                                    letterSpacing: "0.3px",
                                    color: "#686f77"
                                  }}>
                                      {i18n("dashboard_futur", entryState.translationKey)}
                                  </span>
                                </Box>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 0,
                                  py: 0.5,
                                  borderBottom: "none",
                                  color: "secondary.lighter",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }}
                              >
                                <Typography variant="tableCell">
                                  {entry.advisorName}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 0,
                                  py: 0.5,
                                  borderBottom: "none",
                                  color: "secondary.lighter"
                                }}
                              >
                                <Typography variant="tableCell">
                                  {entry.ssn}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 0,
                                  py: 0.5,
                                  borderBottom: "none",
                                  color: "secondary.lighter",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }}
                              >
                                <Typography variant="tableCell">
                                  {!isCustomerModal ? (
                                    <Link variant="tableCell"
                                      href="#"
                                      onClick={handleClientClick(entry.ssn)}
                                      sx={{
                                        textDecoration: "none", "span": {
                                          textDecoration: "underline"
                                        }
                                      }}
                                    >
                                      <span>{entry.name}</span>
                                    </Link>
                                  ) : (
                                    <span>{entry.name}</span>
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 0,
                                  py: 0.5,
                                  borderBottom: "none",
                                  color: "secondary.lighter",
                                }}
                              >
                                <Typography variant="tableCell">
                                  {entry.created}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 0,
                                  pr: 1,
                                  py: 0.5,
                                  borderBottom: "none",
                                  color: "secondary.lighter",
                                  textAlign: "right"
                                }}
                              >
                                <Typography variant="tableCell">
                                  {entry.sign_status_doc}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {entry?.toggled && (
                              <ExpandableRow
                                type={type}
                                colSpan={columns.length}
                                isAdviceSession={isAdviceSession}
                                whitelabel={whitelabel}
                                entry={entry}
                                user={user}
                                onAction={onAction}
                                currentAction={currentAction}
                                resetPaginationAndFilters={resetPaginationAndFilters}
                              />
                            )}                  
                          </Fragment>
                        )
                      })
                    }
                  </Fragment>
              )}
              </TableBody>
            </Table>
          </Box>
          <CustomTableFooter
            loaded={loaded}
            type="futur"
            totalRecords={numCases}
            data={copyList}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage === -1 && numCases > 100 ? 100 : rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            handleChangePage={handleChangePage}
          />
        </TableContainer>
      </Box>
      {(customerModalOpen && selectedCustomer) && (
        <CustomerModal
          view="overview"
          visible={true}
          user={user}
          partner={partner}
          advisor={advisor}
          client={selectedCustomer}
          onClose={handleClientModalClose}
          updateCustomers={updateCustomers}
          caseFilterContext={caseFilterContext}
          addCustomerModalOpen={addCustomerModalOpen}
          toggleAddCustomerModal={toggleAddCustomerModal}
          setCustomerState={setCustomerState}
          onWorkflowSwitched={onWorkflowSwitched}
          nextStepOptions={nextStepOptions}
          setSelectedCustomer={setSelectedCustomer}
        />
      )}
      {addCustomerModalOpen && (
        <AddCustomerModal
          open={addCustomerModalOpen}
          toggleModal={toggleAddCustomerModal}
          customer={selectedCustomer}
          userData={{personal_number: advisor.ssn, role: advisor.role}}
          updateCustomers={updateCustomers}
          allowNoCustomerId={partner._data.allowNoCustomerId}
        />
      )}
    </ContentBox>
  );
};

export default FuturTable;